import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	OnInit,
	HostListener,
	signal,
} from '@angular/core';
import { ESigningPageHeaderComponent } from '../envelope-draft/e-signing-page-header/e-signing-page-header.component';
import { STATUSES } from '@models/statuses';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs';
import { CommonModule } from '@angular/common';
import { EnvelopeViewComponent } from '../envelope-view/envelope-view.component';
import { EnvelopeDraftComponent } from '../envelope-draft/envelope-draft.component';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { select } from '@ngxs/store';
import { SigningElementPackageViewModelComplete } from '@models/e-signing';
import { EnvelopeService } from '@services/envelope/envelope.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ESigningBlockChange } from '../envelope-draft/e-signing-canvas/e-signing-canvas.component';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { ESigningViewTab } from './e-signing-view-tab/e-signing-view-tab.component';
import { BusinessState } from 'src/app/shared/state/business/business.state';

@Component({
	selector: 'app-envelope',
	imports: [
		ESigningPageHeaderComponent,
		EnvelopeDraftComponent,
		EnvelopeViewComponent,
		IconsModule,
		CommonModule,
	],
	templateUrl: './envelope.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'flex flex-col h-full' },
})
export class EnvelopeComponent implements OnInit {
	private envelopeService = inject(EnvelopeService);
	private activatedRoute = inject(ActivatedRoute);
	STATUSES = STATUSES;
	matterId_ = select(SelectedMatter.id);
	isConnectedToSignIt_ = select(BusinessState.isSignITSelected);
	envelopeId_ = toSignal(
		this.activatedRoute.paramMap.pipe(map((params) => params.get('packageId'))),
	);

	public envelope_ = computed<SigningElementPackageViewModelComplete | null>(
		() =>
			this.envelopeService
				.envelopeConfigs_()
				?.find((envelope) => envelope.id === this.envelopeId_()) ?? null,
	);

	envelopeStatus_ = computed(
		() => this.envelope_()?.status ?? STATUSES.LOADING,
	);

	documentNames_ = computed(
		() => this.envelope_()?.contents?.map((doc) => doc.title) ?? [],
	);

	participatingRecipients_ = computed(
		() =>
			this.envelope_()?.recipients?.filter(
				(recipient) => recipient.isParticipating,
			) ?? [],
	);

	isSnapshotting_ = signal(false);

	packageName_ = computed(() =>
		this.envelopeService.getOriginalPackageName(this.envelopeId_() ?? ''),
	);

	async ngOnInit() {
		// Check if initial tab is ESIGNING to set loading state early
		const initialTab = this.activatedRoute.snapshot.queryParamMap?.get('tab');
		const isESigningTab =
			initialTab === ESigningViewTab.ESIGNING || !initialTab; // Default is ESIGNING if no tab param

		// Check if we should skip snapshot (coming from "Prepare for E-signing")
		const skipSnapshot =
			this.activatedRoute.snapshot.queryParamMap?.get('skipSnapshot') ===
			'true';

		// Set loading state immediately if entering ESIGNING tab (unless skipping snapshot)
		if (isESigningTab && !skipSnapshot) {
			this.isSnapshotting_.set(true);
		}

		await this.envelopeService.getEnvelopeViewModel(
			this.matterId_(),
			this.envelopeId_(),
		);

		if (
			isESigningTab &&
			this.envelopeStatus_() === STATUSES.DRAFT &&
			!skipSnapshot
		) {
			await this.handleSnapshotForSigning();
		} else {
			// Reset loading state if we're not actually snapshotting
			this.isSnapshotting_.set(false);
		}

		if (
			!this.isConnectedToSignIt_() &&
			this.envelopeStatus_() === STATUSES.DRAFT
		) {
			await this.envelopeService.convertESigningToStandardPackage(
				this.envelopeId_(),
				this.matterId_(),
			);
		}
	}

	@HostListener('window:focus')
	async onWindowFocus() {
		await this.envelopeService.getEnvelopeViewModel(
			this.matterId_(),
			this.envelopeId_(),
		);
	}

	eSigningBlockChangeHandler = (blockChange: ESigningBlockChange) => {
		this.envelopeService.persistBlockChange(
			this.matterId_(),
			this.envelopeId_(),
			blockChange,
		);
	};

	onEnvelopeNameChange(name: string) {
		this.envelopeService.renameEnvelope(this.envelopeId_(), name);
	}

	onSetIsSignatureDrawOnly(isDrawOnly: boolean) {
		this.envelopeService.setIsSignatureDrawOnly(this.envelopeId_(), isDrawOnly);
	}

	editSignersHandler() {
		this.envelopeService.openEditRecipientModal(this.envelopeId_());
	}

	async onTabChanged(event: { from: ESigningViewTab; to: ESigningViewTab }) {
		if (event.to === ESigningViewTab.ESIGNING) {
			// Set loading state immediately when switching to ESIGNING tab
			this.isSnapshotting_.set(true);
			await this.handleSnapshotForSigning();
		}
	}

	private async handleSnapshotForSigning() {
		try {
			const participatingClientIds = this.participatingRecipients_().map(
				(recipient) => ({
					clientId: recipient.id,
					participantType: recipient.participantType,
				}),
			);

			const success = await this.envelopeService.snapshotPackageForSigning(
				this.matterId_(),
				this.envelopeId_(),
				participatingClientIds,
			);

			if (success) {
				await this.envelopeService.getEnvelopeViewModel(
					this.matterId_(),
					this.envelopeId_(),
				);
			}
		} finally {
			this.isSnapshotting_.set(false);
		}
	}
}
